import type { ReactElement } from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { formatCurrency } from "@/lib/formatting/currency";
import {
  formatTransactionDate,
  getProviderLabel,
} from "@/modules/transactions/lib/transaction-display";
import { TransactionStatusPill } from "@/modules/transactions/components/TransactionStatusPill";
import type { TransactionsListProps } from "@/modules/transactions/types/transactions.types";

// Order · Provider · Amount · Status · Date — five tracks. The Order track is
// widest because it carries two lines (order code + gateway txn id).
const GRID =
  "[grid-template-columns:minmax(0,1.7fr)_minmax(0,1fr)_minmax(0,1.1fr)_minmax(0,1.1fr)_minmax(0,1fr)]";

// Five columns need ~700px. Inside the seller content column (the sidebar takes
// 264px) that is only comfortably available at `xl`, so the column layout starts
// there and every narrower width uses the two-line rows below. This surface
// never scrolls sideways — matching DashboardProductListCard rather than the
// orders table's `overflow-x-auto` fallback.

/** Order code + gateway txn id. The code links to the order; the id is quiet
 * metadata. Both are LTR identifiers, so both are wrapped in `<bdi>` to keep
 * them intact inside Arabic (RTL) text. */
function OrderCell({
  orderCode,
  providerTxnId,
  href,
}: {
  orderCode: string;
  providerTxnId: string | null;
  href: string;
}): ReactElement {
  return (
    <span className="flex min-w-0 flex-col">
      <Link
        to={href}
        // Raised above the row-wide overlay button so clicking the code
        // navigates to the order instead of opening the transaction drawer.
        // `pointer-events-auto` is scoped to the link alone: the rest of the
        // cell stays click-through so it still opens the drawer.
        className="pointer-events-auto relative z-10 w-fit max-w-full truncate text-[12.5px] font-semibold text-[color:var(--text-primary)] underline-offset-2 hover:text-[color:var(--accent-600)] hover:underline"
      >
        <bdi>#{orderCode}</bdi>
      </Link>
      {providerTxnId ? (
        <span className="mt-px truncate font-mono text-[11px] text-[color:var(--text-muted)]">
          <bdi>{providerTxnId}</bdi>
        </span>
      ) : null}
    </span>
  );
}

/**
 * Store-wide transactions as a full-width row list, styled after
 * DashboardProductListCard so it reads as the same system as the other seller
 * list surfaces: card shell, `--soft` header strip, `--hair` dividers, hover.
 *
 * Each row has two interactive targets — the row itself (an overlay button that
 * opens the transaction drawer) and the order code (a link to the order). The
 * overlay is a real `<button>` rather than a wrapping one, because a `<Link>`
 * cannot legally nest inside a button.
 *
 * Direction: grid tracks and flex rows follow the ambient `dir`; the only
 * directional utilities used are logical (`ms-auto`, `pe-3`, `text-end`).
 */
export function TransactionsList({
  items,
  locale,
  orderHref,
  onOpenTransaction,
}: TransactionsListProps): ReactElement {
  const t = useTranslations();

  return (
    <div className="overflow-hidden rounded-[18px] border border-[color:var(--line2)] bg-[color:var(--card)]">
      {/* Column headers only make sense next to the column layout. */}
      <div
        className={`hidden ${GRID} border-b border-[color:var(--line)] bg-[color:var(--soft)] px-[18px] py-[11px] text-[12px] font-semibold text-[color:var(--text-muted)] xl:grid`}
      >
        <span>{t(keys.transactions.colOrder)}</span>
        <span>{t(keys.transactions.colProvider)}</span>
        <span>{t(keys.transactions.colAmount)}</span>
        <span>{t(keys.transactions.colStatus)}</span>
        <span className="text-end">{t(keys.transactions.colDate)}</span>
      </div>

      <div>
        {items.map((transaction) => {
          const href = orderHref(transaction.orderId);
          const amountLabel = formatCurrency(
            transaction.amount,
            locale,
            transaction.currency,
          );
          const providerLabel = getProviderLabel(t, transaction.provider);
          const dateLabel = formatTransactionDate(
            transaction.createdAt,
            locale,
          );

          return (
            <div
              key={transaction.id}
              className="relative border-b border-[color:var(--hair)] px-[18px] transition-colors last:border-b-0 hover:bg-[color:var(--soft)] focus-within:bg-[color:var(--soft)]"
            >
              {/* Row-wide click target. Covers the row so a tap anywhere opens
                  the drawer, while the order link above it keeps its own. */}
              <button
                type="button"
                onClick={() => onOpenTransaction(transaction.id)}
                className="absolute inset-0 z-0 h-full w-full cursor-pointer rounded-none outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--accent-500)]"
              >
                <span className="sr-only">
                  {t(keys.transactions.openDetailAria, {
                    code: transaction.orderCode,
                  })}
                </span>
              </button>

              {/* Column layout (xl+) — cell order mirrors the header */}
              <div
                className={`pointer-events-none relative hidden ${GRID} items-center py-[11px] text-[12.5px] xl:grid`}
              >
                <span className="pe-3">
                  <OrderCell
                    orderCode={transaction.orderCode}
                    providerTxnId={transaction.providerTxnId}
                    href={href}
                  />
                </span>
                <span className="truncate pe-3 text-[color:var(--text-secondary)]">
                  {providerLabel}
                </span>
                <span className="truncate pe-3 font-bold text-[color:var(--text-primary)]">
                  <bdi>{amountLabel}</bdi>
                </span>
                <span className="pe-3">
                  <TransactionStatusPill status={transaction.status} />
                </span>
                <span className="truncate text-end text-[color:var(--text-muted)]">
                  {dateLabel}
                </span>
              </div>

              {/* Two-line layout (below xl) — no horizontal scrolling.
                  Line 1: order + amount · Line 2: provider · date + status. */}
              <div className="pointer-events-none relative flex flex-col gap-2 py-3 xl:hidden">
                <div className="flex items-start gap-2.5">
                  <span className="min-w-0 flex-1">
                    <OrderCell
                      orderCode={transaction.orderCode}
                      providerTxnId={transaction.providerTxnId}
                      href={href}
                    />
                  </span>
                  <span className="shrink-0 text-[13px] font-bold text-[color:var(--text-primary)]">
                    <bdi>{amountLabel}</bdi>
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                  <span className="min-w-0 truncate text-[11.5px] text-[color:var(--text-muted)]">
                    {providerLabel}
                  </span>
                  <span
                    aria-hidden="true"
                    className="text-[color:var(--text-disabled)]"
                  >
                    ·
                  </span>
                  <span className="min-w-0 truncate text-[11.5px] text-[color:var(--text-muted)]">
                    {dateLabel}
                  </span>
                  <span className="ms-auto">
                    <TransactionStatusPill status={transaction.status} />
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
